import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { BandSourceSpec, BandSpec } from "../ChartSpec";
import { detectEvents } from "./detect-events";
import {
    fetchLegacyZoneHistory,
    LEGACY_ZONE_HISTORY_DETECTORS,
    type LegacyZoneHistory,
} from "./legacy-zone-history-adapter";
import { padEventWindow } from "./pad-event-window";
import type { BandData, TimeEvent } from "./types";

// One useQuery per source rather than useQueries: useQueries starts a fresh observer when the key
// changes, so placeholderData would have nothing to keep. Two sources on one zone share the cache entry.
function useZoneHistory(zoneId: string | undefined, padded: { startMs: number; endMs: number }) {
    return useQuery({
        queryKey: ["legacy-zone-history", zoneId, padded.startMs, padded.endMs] as const,
        queryFn: () => fetchLegacyZoneHistory(zoneId ?? "", padded.startMs, padded.endMs),
        enabled: zoneId !== undefined,
        // Old data stands in only if it reaches back as far, or missing days draw as no irrigation.
        placeholderData: (previous, previousQuery) => {
            const [, previousZoneId, previousStartMs] = previousQuery?.queryKey ?? [];
            return previousZoneId === zoneId && previousStartMs !== undefined && previousStartMs <= padded.startMs
                ? previous
                : undefined;
        },
    });
}

/** Fetches and edge-detects a band's sources. See README.md. */
export function useBandData(bandSpec: BandSpec | undefined, startMs: number, endMs: number): BandData | undefined {
    const padded = padEventWindow(startMs, endMs);
    const scheduledQuery = useZoneHistory(bandSpec?.scheduled.zoneId, padded);
    const actualQuery = useZoneHistory(bandSpec?.actual.zoneId, padded);
    const queries = [scheduledQuery, actualQuery];
    const scheduledHistory = scheduledQuery.data;
    const actualHistory = actualQuery.data;
    const isLoading = queries.some((query) => query.isLoading);
    // A failed refetch of the same window still has its data, so draw that instead of the error.
    const isError = queries.some((query) => query.isError && query.data === undefined);

    return useMemo(() => {
        if (!bandSpec) return undefined;
        const nowMs = Date.now();
        const eventsFor = (source: BandSourceSpec, history: LegacyZoneHistory | undefined): TimeEvent[] => {
            if (!history) return [];
            return detectEvents(
                history[source.signal],
                LEGACY_ZONE_HISTORY_DETECTORS[source.signal],
                { startMs, endMs },
                nowMs,
            );
        };
        return {
            scheduled: eventsFor(bandSpec.scheduled, scheduledHistory),
            actual: eventsFor(bandSpec.actual, actualHistory),
            isLoading,
            isError,
        };
    }, [bandSpec, scheduledHistory, actualHistory, isLoading, isError, startMs, endMs]);
}
